import { useEffect, useMemo, useRef, useState } from "react";
import { DADES, GRUPOS, type Grupo, type Modelo } from "./model";

// ─── Format i peces ───────────────────────────────────────────────────────────

export const eur = (n: number) => new Intl.NumberFormat("ca-ES", { maximumFractionDigits: 0 }).format(n) + " €";
export const int = (n: number) => new Intl.NumberFormat("ca-ES", { maximumFractionDigits: 0 }).format(n);
const pct = (n: number) => `${n > 0 ? "+" : ""}${n.toFixed(1).replace(".", ",")}%`;
const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

export function Card({ title, sub, children, right }: { title: string; sub?: React.ReactNode; children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-border bg-background p-5 sm:p-6 min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          {sub && <p className="text-sm text-muted-foreground mt-0.5 text-pretty">{sub}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function Stat({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="rounded-2xl border border-border p-4 min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold text-foreground tabular-nums mt-1 truncate">{value}</p>
      {note && <p className="text-xs text-muted-foreground mt-1 text-pretty">{note}</p>}
    </div>
  );
}

/** Barres horitzontals d'una sola sèrie, amb el valor al final de cada barra. */
function Bars({ rows, format, color = "var(--s1)" }: { rows: { label: string; value: number; hint?: string }[]; format: (n: number) => string; color?: string }) {
  const max = Math.max(...rows.map((r) => Math.abs(r.value)), 1);
  return (
    <ol className="space-y-1.5">
      {rows.map((r) => (
        <li key={r.label} className="grid grid-cols-[minmax(0,9rem)_1fr] sm:grid-cols-[minmax(0,12rem)_1fr] items-center gap-3 text-sm" title={r.hint ?? `${r.label}: ${format(r.value)}`}>
          <span className="truncate text-foreground">{r.label}</span>
          <span className="flex items-center gap-2 min-w-0">
            <span className="h-3.5 rounded-r-[4px] shrink-0" style={{ width: `calc((100% - 5.5rem) * ${Math.abs(r.value) / max})`, minWidth: 2, background: color }} />
            <span className="text-muted-foreground tabular-nums text-xs whitespace-nowrap">{format(r.value)}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

export function Chips<T extends string>({ options, value, onChange }: { options: readonly T[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5" role="toolbar">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          aria-pressed={value === o}
          onClick={() => onChange(o)}
          className={`h-8 px-3 rounded-full border text-xs font-medium transition-colors ${value === o ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

// ─── Dades derivades (el mateix que faria un JOIN) ────────────────────────────

const marcaNom = new Map(DADES.Marcas.map((m) => [m.MarcaID, m.Nombre]));
const comb = new Map(DADES.Combustibles.map((c) => [c.CombustibleID, c]));
const preu = (edicio: number) => new Map(DADES.Precios.filter((p) => p.EdicionID === edicio).map((p) => [p.ModeloID, p.Precio]));
const marc = preu(1);
const juliol = preu(2);

type Fila = Modelo & { marca: string; combustible: string; grupo: Grupo; preu?: number };
const files: Fila[] = DADES.Modelos.map((m) => ({
  ...m,
  marca: marcaNom.get(m.MarcaID) ?? m.MarcaID,
  combustible: comb.get(m.CombustibleID)!.Nombre,
  grupo: comb.get(m.CombustibleID)!.Grupo,
  preu: juliol.get(m.ModeloID),
}));
/** Els que són a l'edició de juliol: la llista de models a la venda. */
const aLaVenda = files.filter((f) => f.preu);

/** Famílies més detallades per al recompte. */
function familia(fuel: string): string {
  const f = fuel.toLowerCase();
  if (/phev|endollable/.test(f)) return "Híbrid endollable";
  if (/mhev|mild/.test(f)) return "Microhíbrid";
  if (/hev|híbrid/.test(f)) return "Híbrid";
  if (/elèctric/.test(f)) return "Elèctric";
  // «gas» sol també és a «gasolina».
  if (/glp|gnc|gas natural|gas liquat/.test(f) && !/gasolina/.test(f)) return "Gas";
  if (/dièsel|diesel/.test(f)) return "Dièsel";
  return "Gasolina";
}

const GRUP_COLOR: Record<Grupo, string> = { Elèctric: "var(--s1)", Electrificat: "var(--s2)", Combustió: "var(--s3)" };

// ─── Preu i potència ──────────────────────────────────────────────────────────

function Scatter() {
  const [on, setOn] = useState<Record<Grupo, boolean>>({ Elèctric: true, Electrificat: true, Combustió: true });
  const [hover, setHover] = useState<Fila | null>(null);
  const [maxPreu, setMaxPreu] = useState<"Fins a 150.000 €" | "Tots">("Fins a 150.000 €");
  const points = aLaVenda.filter((m) => m.Potencia && on[m.grupo] && (maxPreu === "Tots" || m.preu! <= 150_000));
  const box = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(640);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setW(Math.round(e!.contentRect.width) || 640));
    ro.observe(box.current!);
    return () => ro.disconnect();
  }, []);
  const H = Math.round(Math.min(380, Math.max(260, W * 0.6))), L = 44, B = 30, T = 14, R = 14;
  const xMax = Math.max(...points.map((p) => p.Potencia!), 100);
  const yMax = Math.max(...points.map((p) => p.preu!), 10_000);
  const x = (v: number) => L + (v / xMax) * (W - L - R);
  const y = (v: number) => H - B - (v / yMax) * (H - B - T);
  /** Marques de l'eix en números rodons (1, 2, 2,5 o 5 per una potència de 10). */
  const ticks = (max: number) => {
    const raw = max / 4;
    const p = 10 ** Math.floor(Math.log10(raw));
    const step = [1, 2, 2.5, 5, 10].map((k) => k * p).find((s) => s >= raw)!;
    return Array.from({ length: Math.floor(max / step) + 1 }, (_, i) => i * step);
  };
  const svg = useRef<SVGSVGElement>(null);

  function move(e: React.PointerEvent) {
    const r = svg.current!.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    const py = ((e.clientY - r.top) / r.height) * H;
    let best: Fila | null = null;
    let d = 18 ** 2;
    for (const p of points) {
      const dd = (x(p.Potencia!) - px) ** 2 + (y(p.preu!) - py) ** 2;
      if (dd < d) (d = dd), (best = p);
    }
    setHover(best);
  }

  return (
    <Card
      title="Preu i potència"
      sub={`Cada punt és un model (${points.length}), amb el preu de juliol. Passa-hi per sobre per veure quin és.`}
      right={<Chips<"Fins a 150.000 €" | "Tots"> options={["Fins a 150.000 €", "Tots"] as const} value={maxPreu} onChange={setMaxPreu} />}
    >
      <div className="flex flex-wrap gap-2 mb-3" aria-label="Llegenda">
        {GRUPOS.map((g) => (
          <button key={g} type="button" aria-pressed={on[g]} onClick={() => setOn((o) => ({ ...o, [g]: !o[g] }))} className={`flex items-center gap-1.5 h-8 px-3 rounded-full border border-border text-xs ${on[g] ? "text-foreground" : "text-muted-foreground opacity-50"}`}>
            <span className="size-2.5 rounded-full" style={{ background: GRUP_COLOR[g] }} />
            {g}
          </button>
        ))}
      </div>
      <div className="relative" ref={box}>
        <svg ref={svg} width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="block w-full h-auto touch-none" onPointerMove={move} onPointerLeave={() => setHover(null)} role="img" aria-label="Gràfic de dispersió de preu i potència">
          {ticks(yMax).map((t) => (
            <g key={`y${t}`}>
              <line x1={L} x2={W - R} y1={y(t)} y2={y(t)} stroke="var(--grid)" />
              <text x={L - 6} y={y(t) + 4} textAnchor="end" className="fill-muted-foreground" fontSize="11">
                {t >= 1000 ? `${Math.round(t / 1000)}k` : t}
              </text>
            </g>
          ))}
          {ticks(xMax).map((t) => (
            <text key={`x${t}`} x={x(t)} y={H - B + 16} textAnchor="middle" className="fill-muted-foreground" fontSize="11">
              {Math.round(t)}
            </text>
          ))}
          <text x={W - R} y={H - 4} textAnchor="end" className="fill-muted-foreground" fontSize="11">CV</text>
          <text x={4} y={T - 2} className="fill-muted-foreground" fontSize="11">€</text>
          {points.map((p) => (
            <circle key={p.ModeloID} cx={x(p.Potencia!)} cy={y(p.preu!)} r={hover?.ModeloID === p.ModeloID ? 6 : 4} fill={GRUP_COLOR[p.grupo]} fillOpacity={hover && hover.ModeloID !== p.ModeloID ? 0.35 : 0.8} stroke="var(--background)" strokeWidth={hover?.ModeloID === p.ModeloID ? 2 : 1} />
          ))}
        </svg>
        {hover && (
          <div
            className="pointer-events-none absolute z-10 rounded-lg border border-border bg-background px-3 py-2 text-xs shadow-lg"
            style={{ left: `min(calc(${(x(hover.Potencia!) / W) * 100}% + 10px), calc(100% - 12rem))`, top: `${(y(hover.preu!) / H) * 100}%` }}
          >
            <p className="font-semibold text-foreground">{hover.marca} {hover.Nombre}</p>
            <p className="text-muted-foreground">{hover.combustible}</p>
            <p className="text-foreground tabular-nums">{hover.Potencia} CV · {eur(hover.preu!)}</p>
          </div>
        )}
      </div>
    </Card>
  );
}

// ─── Rànquing de marques ──────────────────────────────────────────────────────

const METRIQUES = ["Models", "Preu mitjà", "Potència mitjana", "€ per CV"] as const;
type Metrica = (typeof METRIQUES)[number];

function Marques() {
  const [m, setM] = useState<Metrica>("Preu mitjà");
  const [ordre, setOrdre] = useState<"Més" | "Menys">("Més");
  const rows = useMemo(() => {
    const by = new Map<string, Fila[]>();
    for (const f of aLaVenda) by.set(f.marca, [...(by.get(f.marca) ?? []), f]);
    const out = Array.from(by).flatMap(([label, fs]) => {
      const cv = fs.flatMap((f) => (f.Potencia ? [f.Potencia] : []));
      const eurCv = fs.flatMap((f) => (f.Potencia ? [f.preu! / f.Potencia] : []));
      const value = m === "Models" ? fs.length : m === "Preu mitjà" ? avg(fs.map((f) => f.preu!)) : m === "Potència mitjana" ? avg(cv) : avg(eurCv);
      // Amb un sol model la mitjana no diu gaire de la marca.
      return value && (m === "Models" || fs.length >= 2) ? [{ label, value, hint: `${label}: ${fs.length} models` }] : [];
    });
    out.sort((a, b) => (ordre === "Més" ? b.value - a.value : a.value - b.value));
    return out.slice(0, 15);
  }, [m, ordre]);
  const format = m === "Models" ? int : m === "Potència mitjana" ? (n: number) => `${int(n)} CV` : eur;
  return (
    <Card title="Marques" sub="Les 15 primeres. A les mitjanes només hi entren les marques amb dos models o més." right={<Chips<"Més" | "Menys"> options={["Més", "Menys"] as const} value={ordre} onChange={setOrdre} />}>
      <div className="mb-4">
        <Chips<Metrica> options={METRIQUES} value={m} onChange={setM} />
      </div>
      <Bars rows={rows} format={format} />
    </Card>
  );
}

// ─── Canvis de preu ───────────────────────────────────────────────────────────

function CanvisPreu() {
  const canvis = files
    .filter((f) => marc.get(f.ModeloID) && f.preu && marc.get(f.ModeloID) !== f.preu)
    .map((f) => {
      const a = marc.get(f.ModeloID)!;
      return { label: `${f.marca} ${f.Nombre} · ${f.combustible}`, value: ((f.preu! - a) / a) * 100, hint: `${f.marca} ${f.Nombre} (${f.combustible}): ${eur(a)} → ${eur(f.preu!)}` };
    });
  const nous = files.filter((f) => f.preu && !marc.get(f.ModeloID)).length;
  const fora = files.filter((f) => !f.preu && marc.get(f.ModeloID)).length;
  const igual = files.filter((f) => f.preu && marc.get(f.ModeloID) === f.preu).length;
  const pujades = [...canvis].sort((a, b) => b.value - a.value).filter((c) => c.value > 0).slice(0, 8);
  const baixades = [...canvis].sort((a, b) => a.value - b.value).filter((c) => c.value < 0).slice(0, 8);
  return (
    <Card title="Com han canviat els preus" sub={`De l'edició de març a la de juliol. Dels que han canviat, la variació mitjana és ${pct(avg(canvis.map((c) => c.value)))}.`}>
      <div className="flex flex-wrap gap-2 mb-5">
        {[["Han canviat", canvis.length], ["Igual", igual], ["Nous", nous], ["Ja no hi són", fora]].map(([e, n]) => (
          <span key={e} className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground">
            {e}: <b className="text-foreground tabular-nums">{n}</b>
          </span>
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div>
          <h4 className="text-sm font-medium mb-2 text-foreground">Els que més pugen</h4>
          <Bars rows={pujades} format={pct} color="var(--up)" />
        </div>
        <div>
          <h4 className="text-sm font-medium mb-2 text-foreground">Els que més baixen</h4>
          <Bars rows={baixades} format={pct} color="var(--down)" />
        </div>
      </div>
    </Card>
  );
}

// ─── Panell ───────────────────────────────────────────────────────────────────

export function Panell() {
  const preus = aLaVenda.map((f) => f.preu!);
  const families = Object.entries(aLaVenda.reduce<Record<string, number>>((acc, m) => ((acc[familia(m.combustible)] = (acc[familia(m.combustible)] ?? 0) + 1), acc), {}))
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);
  const mat = DADES.Matriculaciones;
  const topMat = Object.entries(mat.reduce<Record<string, number>>((acc, m) => {
    const k = m.MarcaID ? marcaNom.get(m.MarcaID)! : "Fora del catàleg";
    acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {}))
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);
  const dates = mat.map((m) => m.Fecha).sort();

  return (
    <div className="space-y-6">
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <Stat label="Marques" value={int(DADES.Marcas.length)} note={`+${int(DADES.Descatalogados.length)} models descatalogats`} />
        <Stat label="Models a la venda" value={int(aLaVenda.length)} note={`${int(DADES.Combustibles.length)} combustibles diferents`} />
        <Stat label="Preu mitjà (juliol)" value={eur(avg(preus))} note={`De ${eur(Math.min(...preus))} a ${eur(Math.max(...preus))}`} />
        <Stat label="Matriculacions" value={int(mat.length)} note={`${dates[0]} → ${dates[dates.length - 1]}`} />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Què es ven" sub="Models a la venda per tipus de motor.">
          <Bars rows={families} format={int} />
        </Card>
        <Card title="Què es matricula" sub={`Marques de les ${mat.length} últimes matrícules.`}>
          <Bars rows={topMat} format={int} />
        </Card>
      </div>
      <Scatter />
      <Marques />
      <CanvisPreu />
    </div>
  );
}
